import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, ExternalLink } from "lucide-react";

interface Tool {
  name: string;
  logo: string;
  url: string;
  pricing: string;
  perUser: string;
  customization: "Unlimited" | "High" | "Medium" | "Limited";
  whiteLabel: boolean | "Enterprise";
  integrations: string;
  bestFor: string;
  isRecommended?: boolean;
}

const tools: Tool[] = [
  {
    name: "Lottae",
    logo: "🏆",
    url: "https://www.lottae.com/pricing",
    pricing: "$7 + $3.50/user",
    perUser: "$3.50",
    customization: "Unlimited",
    whiteLabel: true,
    integrations: "100+",
    bestFor: "Best value + flexibility",
    isRecommended: true,
  },
  {
    name: "Asana",
    logo: "📋",
    url: "https://asana.com",
    pricing: "$0-30.49/mo",
    perUser: "$10.99+",
    customization: "Limited",
    whiteLabel: "Enterprise",
    integrations: "200+",
    bestFor: "Marketing teams",
  },
  {
    name: "Monday.com",
    logo: "📊",
    url: "https://monday.com",
    pricing: "$9-19/user",
    perUser: "$9+",
    customization: "Medium",
    whiteLabel: "Enterprise",
    integrations: "200+",
    bestFor: "Visual workflows",
  },
  {
    name: "Trello",
    logo: "📌",
    url: "https://trello.com",
    pricing: "$0-17.50/user",
    perUser: "$5+",
    customization: "Limited",
    whiteLabel: false,
    integrations: "150+",
    bestFor: "Simple kanban",
  },
  {
    name: "Jira",
    logo: "🔧",
    url: "https://www.atlassian.com/software/jira",
    pricing: "$0-15.25/user",
    perUser: "$7.75+",
    customization: "High",
    whiteLabel: "Enterprise",
    integrations: "1000+",
    bestFor: "Development teams",
  },
  {
    name: "ClickUp",
    logo: "✅",
    url: "https://clickup.com",
    pricing: "$0-19/user",
    perUser: "$7+",
    customization: "High",
    whiteLabel: "Enterprise",
    integrations: "100+",
    bestFor: "All-in-one solution",
  },
  {
    name: "Notion",
    logo: "📝",
    url: "https://notion.so",
    pricing: "$0-15/user",
    perUser: "$10+",
    customization: "Medium",
    whiteLabel: false,
    integrations: "50+",
    bestFor: "Docs + projects",
  },
];

const CustomizationBadge = ({ level }: { level: Tool["customization"] }) => {
  const variants: Record<Tool["customization"], string> = {
    "Unlimited": "bg-primary/20 text-primary border-primary/30",
    "High": "bg-green-500/20 text-green-600 border-green-500/30",
    "Medium": "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
    "Limited": "bg-muted text-muted-foreground border-border",
  };
  
  return (
    <Badge variant="outline" className={variants[level]}>
      {level}
    </Badge>
  );
};

const WhiteLabelCell = ({ value }: { value: boolean | "Enterprise" }) => {
  if (value === true) return <Check className="w-5 h-5 text-primary mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-muted-foreground mx-auto" />;
  return <span className="text-xs text-muted-foreground text-center block">Enterprise only</span>;
};

export const PMToolsComparison = () => {
  return (
    <section className="py-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Feature Comparison Table</CardTitle>
          <p className="text-muted-foreground">
            Side-by-side comparison of pricing, features, and capabilities
          </p>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[140px]">Tool</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Per User</TableHead>
                  <TableHead>Customization</TableHead>
                  <TableHead className="text-center">White-Label</TableHead>
                  <TableHead>Integrations</TableHead>
                  <TableHead className="min-w-[140px]">Best For</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tools.map((tool, index) => {
                  const isCostEffective = parseFloat(tool.perUser.replace(/[^0-9.]/g, '')) <= 7;
                  const isTopPosition = index === 0 && tool.isRecommended;
                  
                  return (
                    <TableRow 
                      key={tool.name}
                      className={tool.isRecommended 
                        ? "border-2 border-primary bg-primary/5 hover:bg-primary/10" 
                        : ""
                      }
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{tool.logo}</span>
                          <a 
                            href={tool.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`hover:text-primary transition-colors ${tool.isRecommended ? "font-bold" : ""}`}
                          >
                            {tool.name}
                            <ExternalLink className="w-3 h-3 inline ml-1 opacity-50" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{tool.pricing}</TableCell>
                      <TableCell className={isCostEffective ? "font-bold text-primary" : ""}>
                        {tool.perUser}
                      </TableCell>
                      <TableCell>
                        <CustomizationBadge level={tool.customization} />
                      </TableCell>
                      <TableCell className="text-center">
                        <WhiteLabelCell value={tool.whiteLabel} />
                      </TableCell>
                      <TableCell>{tool.integrations}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tool.bestFor}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
